import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import * as C from "@/data/content";
import { MENU_COMPLETO } from "@/data/menuCompleto";
import { TEXTS, SETTINGS } from "@/data/texts";
import BAKED from "@/data/baked.json";

export const STATIC = process.env.REACT_APP_STATIC === "1";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND}/api`;

const absolutize = (v) =>
  typeof v === "string"
    ? v.startsWith("/api/files/") ? `${BACKEND}${v}` : v
    : Array.isArray(v)
      ? v.map(absolutize)
      : v && typeof v === "object"
        ? Object.fromEntries(Object.entries(v).map(([k, x]) => [k, absolutize(x)]))
        : v;

export const relativize = (data) => (BACKEND ? JSON.parse(JSON.stringify(data).split(`${BACKEND}/api/files/`).join("/api/files/")) : data);

export const DEFAULTS = {
  TEXTS,
  SETTINGS,
  PILLARS: C.PILLARS,
  WHY: C.WHY,
  HISTORY: C.HISTORY,
  ABOUT_PHOTOS: C.ABOUT_PHOTOS,
  SPECIALTIES: C.SPECIALTIES,
  DISHES: C.DISHES,
  CAKES: C.CAKES,
  EVENTS: C.EVENTS,
  DECOR: C.DECOR,
  MEETINGS: C.MEETINGS,
  STATS: C.STATS,
  ECO: C.ECO,
  GALLERY: C.GALLERY,
  DECORADOS: C.DECORADOS,
  BRANCHES: C.BRANCHES,
  MARQUEE: C.MARQUEE,
  MENU_COMPLETO,
  NAV: C.NAV,
  PRODUCTS: C.PRODUCTS,
};

export const merge = (data) => {
  const d = data && typeof data === "object" ? absolutize(data) : {};
  return { ...DEFAULTS, ...d, TEXTS: { ...TEXTS, ...(d.TEXTS || {}) }, SETTINGS: { ...SETTINGS, ...(d.SETTINGS || {}) } };
};

const Ctx = createContext({ content: DEFAULTS, reload: () => {} });

const loadFonts = (s) => {
  const fams = [s.fontDisplay, s.fontBody].filter(Boolean);
  const href = `https://fonts.googleapis.com/css2?${fams.map((f) => `family=${f.replace(/ /g, "+")}:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500`).join("&")}&display=swap`;
  let link = document.getElementById("dyn-fonts");
  if (!link) {
    link = document.createElement("link");
    link.id = "dyn-fonts";
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }
  link.href = href;
  document.documentElement.style.setProperty("--font-display", `"${s.fontDisplay}"`);
  document.documentElement.style.setProperty("--font-body", `"${s.fontBody}"`);
};

export const ContentProvider = ({ children }) => {
  const [data, setData] = useState(STATIC ? BAKED : null);
  const reload = useCallback(async () => {
    if (STATIC) return;
    try {
      const res = await axios.get(`${API}/content`);
      setData(res.data?.data ?? null);
    } catch {
      setData(null);
    }
  }, []);
  useEffect(() => {
    reload();
  }, [reload]);
  const content = useMemo(() => merge(data), [data]);
  useEffect(() => {
    loadFonts(content.SETTINGS);
  }, [content.SETTINGS]);
  return <Ctx.Provider value={{ content, reload, raw: data }}>{children}</Ctx.Provider>;
};

export const useContent = () => useContext(Ctx).content;

export const useWa = () => {
  const T = useContext(Ctx).content.TEXTS;
  return (key = "wa_msg_default", vars = {}) => {
    let num = String(T.wa_number || "").replace(/\D/g, "");
    if (num.length === 10) num = `52${num}`;
    const msg = Object.entries(vars).reduce((m, [k, v]) => m.split(`{${k}}`).join(v), T[key] || T.wa_msg_default || "");
    return `https://wa.me/${num}?text=${encodeURIComponent(msg)}`;
  };
};
export const useContentCtx = () => useContext(Ctx);

export const rich = (str = "") =>
  String(str)
    .split(/\*(.+?)\*/g)
    .map((part, i) => (i % 2 ? <em key={i} className="text-gold">{part}</em> : part));

const norm = (s = "") => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]/g, "");
export const photoFor = (images = [], name) => {
  const n = norm(name);
  const exact = images.findIndex((im) => norm(im.link || im.cap) === n);
  if (exact >= 0 || !n) return exact;
  return images.findIndex((im) => !im.link && norm(im.cap).length > 5 && n.startsWith(norm(im.cap)));
};
